/**
 * IssueDetails
 *
 * Accordion-based grouped view for intake batch validation issues.
 * Groups issues by type (Errors, Warnings, Routed, etc.) at the top level,
 * then within each type shows row-by-row details in a nested accordion.
 *
 * Adapted from the ValidationDetails pattern in resi-fe-btbhome dataUpload.
 */
import React, { useState, useEffect, useMemo } from 'react'
import classnames from 'classnames/bind'
import * as graph from '@adc/sdk'
import type { IntakeBatch } from '@adc/sdk/types'
import Text from '../elementals/text'
import Icon from '../elementals/icon'
import SearchFilter from '../elementals/searchFilter'
import { Button } from '../elementals'
// @ts-ignore no types for scss modules
import styles from './styles.scss'

const cx = classnames.bind(styles)

// ─── Types ──────────────────────────────────────────────────────────────────────

interface IssueItem {
  column: string
  description: string
}

interface RowGroup {
  rowIndex: number | null
  issues: IssueItem[]
}

interface TypeGroup {
  type: string
  totalIssueCount: number
  rows: RowGroup[]
}

export interface IssueDetailsProps {
  /** The batch results array from IntakeBatch.results */
  results: IntakeBatch['results']
  /** Base elm-id for testing selectors */
  elmId?: string
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

const TYPE_ORDER = ['ERROR', 'WARNING', 'REJECTED', 'ROUTED', 'FLAGGED']

const getTypeIcon = (type: string): { name: string; color: string } => {
  switch (type) {
    case 'ERROR':
    case 'REJECTED':
      return { name: 'circle-exclamation', color: 'error' }
    case 'WARNING':
    case 'FLAGGED':
      return { name: 'triangle-exclamation', color: 'caution' }
    case 'ROUTED':
      return { name: 'arrow-right', color: 'action' }
    default:
      return { name: 'circle-info', color: 'gray' }
  }
}

const getTypeLabel = (type: string): string => {
  switch (type) {
    case 'ERROR':
      return 'Errors'
    case 'WARNING':
      return 'Warnings'
    case 'REJECTED':
      return 'Rejected'
    case 'ROUTED':
      return 'Routed'
    case 'FLAGGED':
      return 'Flagged'
    default:
      return type.charAt(0) + type.slice(1).toLowerCase()
  }
}

// ─── Component ──────────────────────────────────────────────────────────────────

/**
 * Groups issues by type at top level, then by row within each type.
 * Each type section is a collapsible accordion containing row accordions.
 */
export const IssueDetails: React.FC<IssueDetailsProps> = ({
  results,
  elmId = 'issue-details',
}) => {
  const [expandedTypes, setExpandedTypes] = useState<string[]>([])
  const [expandedRows, setExpandedRows] = useState<Record<string, number[]>>({})
  const [textFilter, setTextFilter] = useState('')

  // Build type groups from results
  const typeGroups: TypeGroup[] = useMemo(() => {
    const groupMap: Map<string, Map<string, RowGroup>> = new Map()

    ;(results || []).forEach(result => {
      if (!result || result.result_type === 'SUCCESS') return
      const type = result.result_type || 'UNKNOWN'

      if (!groupMap.has(type)) groupMap.set(type, new Map())
      const rowMap = groupMap.get(type)!

      ;(result.details || []).forEach(detail => {
        if (!detail) return
        const rowKey = String(detail.row_index ?? 'null')
        const cols = detail.columns_detail || []

        // Flagged rows whose values could not be standardized get a suffixed
        // description so the cause is visible inline.
        const isStandardizationFlag =
          type === graph.enums.RowStatus.Flagged &&
          detail.cause_code === graph.enums.CauseCode.Standardization

        const describe = (description: string): string =>
          isStandardizationFlag
            ? `${description} missing from standardizations`
            : description

        const issues: IssueItem[] =
          cols.length === 0
            ? [{ column: '—', description: describe('—') }]
            : cols.map(col => ({
                column: col?.column || '—',
                description: describe(col?.description || '—'),
              }))

        if (rowMap.has(rowKey)) {
          rowMap.get(rowKey)!.issues.push(...issues)
        } else {
          rowMap.set(rowKey, {
            rowIndex: detail.row_index ?? null,
            issues,
          })
        }
      })
    })

    // Sort types by defined order, build final structure
    return Array.from(groupMap.entries())
      .sort(([a], [b]) => {
        const ai = TYPE_ORDER.indexOf(a)
        const bi = TYPE_ORDER.indexOf(b)
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
      })
      .map(([type, rowMap]) => {
        const rows = Array.from(rowMap.values()).sort((a, b) => {
          if (a.rowIndex === null) return 1
          if (b.rowIndex === null) return -1
          return a.rowIndex - b.rowIndex
        })
        const totalIssueCount = rows.reduce(
          (sum, r) => sum + r.issues.length,
          0
        )
        return { type, totalIssueCount, rows }
      })
  }, [results])

  // Filter rows by keyword
  const filteredTypeGroups = useMemo(() => {
    if (!textFilter.length) return typeGroups
    const lower = textFilter.toLowerCase()
    return typeGroups
      .map(group => ({
        ...group,
        rows: group.rows.filter(row =>
          row.issues.some(
            issue =>
              issue.column.toLowerCase().includes(lower) ||
              issue.description.toLowerCase().includes(lower)
          )
        ),
      }))
      .filter(group => group.rows.length > 0)
  }, [typeGroups, textFilter])

  // Auto-expand all when filtering
  useEffect(() => {
    if (textFilter.length) {
      setExpandedTypes(filteredTypeGroups.map(g => g.type))
      const allRows: Record<string, number[]> = {}
      filteredTypeGroups.forEach(g => {
        allRows[g.type] = g.rows.map((_, idx) => idx)
      })
      setExpandedRows(allRows)
    }
  }, [textFilter, filteredTypeGroups.length])

  const toggleType = (type: string) => {
    setExpandedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    )
  }

  const toggleRow = (type: string, index: number) => {
    setExpandedRows(prev => {
      const current = prev[type] || []
      return {
        ...prev,
        [type]: current.includes(index)
          ? current.filter(i => i !== index)
          : [...current, index],
      }
    })
  }

  const toggleAll = () => {
    const allExpanded =
      expandedTypes.length === filteredTypeGroups.length &&
      filteredTypeGroups.every(
        g => (expandedRows[g.type] || []).length === g.rows.length
      )

    if (allExpanded) {
      setExpandedTypes([])
      setExpandedRows({})
    } else {
      setExpandedTypes(filteredTypeGroups.map(g => g.type))
      const allRows: Record<string, number[]> = {}
      filteredTypeGroups.forEach(g => {
        allRows[g.type] = g.rows.map((_, idx) => idx)
      })
      setExpandedRows(allRows)
    }
  }

  if (typeGroups.length === 0) return null

  return (
    <div className={cx('issue-details')} data-elm-id={elmId}>
      <Text size='small' weight='medium' color='gray'>
        Issue Details
      </Text>

      {/* Toolbar: filter + expand/collapse */}
      <div className={cx('issue-details__toolbar')}>
        <div className={cx('issue-details__filter')}>
          <SearchFilter
            elmId={`${elmId}-filter`}
            placeholder='Filter by keyword...'
            onChange={(
              _e: React.ChangeEvent<HTMLInputElement>,
              data: Record<string, unknown>
            ) => setTextFilter(String(data.value ?? ''))}
            onResetIconClick={() => setTextFilter('')}
            value={textFilter}
            width='fluid'
          />
        </div>
        <Button
          type='button'
          role='tertiary'
          onClick={toggleAll}
          size='small'
          data-elm-id={`${elmId}-toggle-all`}
        >
          {expandedTypes.length === filteredTypeGroups.length
            ? 'Collapse All'
            : 'Expand All'}
        </Button>
      </div>

      {/* No results */}
      {filteredTypeGroups.length === 0 && textFilter.length > 0 && (
        <div className={cx('issue-details__no-results')}>
          <Text size='small' color='gray'>
            No results for &ldquo;{textFilter}&rdquo;
          </Text>
        </div>
      )}

      {/* Type-level accordions */}
      <div className={cx('issue-details__type-list')}>
        {filteredTypeGroups.map(group => {
          const isTypeExpanded = expandedTypes.includes(group.type)
          const typeIcon = getTypeIcon(group.type)
          const typeRowsExpanded = expandedRows[group.type] || []

          return (
            <div
              key={group.type}
              className={cx('issue-details__type-group', {
                'issue-details__type-group--expanded': isTypeExpanded,
              })}
            >
              {/* Type header */}
              <button
                type='button'
                className={cx('issue-details__type-header')}
                onClick={() => toggleType(group.type)}
                aria-expanded={isTypeExpanded}
                data-elm-id={`${elmId}-type-${group.type.toLowerCase()}`}
              >
                <div className={cx('issue-details__type-header-left')}>
                  <Icon
                    name={typeIcon.name}
                    iconStyle='solid'
                    size='small'
                    color={typeIcon.color as any}
                  />
                  <Text size='small' weight='medium'>
                    {getTypeLabel(group.type)}
                  </Text>
                  <Text size='mini' color='gray'>
                    ({group.rows.length}{' '}
                    {group.rows.length === 1 ? 'row' : 'rows'},{' '}
                    {group.totalIssueCount}{' '}
                    {group.totalIssueCount === 1 ? 'issue' : 'issues'})
                  </Text>
                </div>
                <Icon
                  name='chevron-down'
                  iconStyle='solid'
                  size='small'
                  color='gray'
                  className={cx('issue-details__chevron', {
                    'issue-details__chevron--expanded': isTypeExpanded,
                  })}
                />
              </button>

              {/* Row-level accordion (nested inside type) */}
              {isTypeExpanded && (
                <div className={cx('issue-details__rows')}>
                  {group.rows.map((row, rowIdx) => {
                    const isRowExpanded = typeRowsExpanded.includes(rowIdx)
                    return (
                      <div
                        key={`${row.rowIndex}-${rowIdx}`}
                        className={cx('issue-details__row-group', {
                          'issue-details__row-group--expanded': isRowExpanded,
                        })}
                      >
                        <button
                          type='button'
                          className={cx('issue-details__row-header')}
                          onClick={() => toggleRow(group.type, rowIdx)}
                          aria-expanded={isRowExpanded}
                          data-elm-id={`${elmId}-row-${group.type}-${
                            row.rowIndex ?? 'none'
                          }`}
                        >
                          <Text size='mini' weight='medium'>
                            Row {row.rowIndex ?? '—'}
                          </Text>
                          <Text size='mini' color='gray'>
                            ({row.issues.length}{' '}
                            {row.issues.length === 1 ? 'issue' : 'issues'})
                          </Text>
                          <Icon
                            name='chevron-down'
                            iconStyle='solid'
                            size='small'
                            color='gray'
                            className={cx('issue-details__chevron', {
                              'issue-details__chevron--expanded': isRowExpanded,
                            })}
                          />
                        </button>

                        {isRowExpanded && (
                          <div className={cx('issue-details__row-content')}>
                            {row.issues.map((issue, issueIdx) => (
                              <div
                                key={issueIdx}
                                className={cx('issue-details__issue-row')}
                                data-elm-id={`${elmId}-issue-${group.type}-${
                                  row.rowIndex ?? 'none'
                                }-${issueIdx}`}
                              >
                                <Text
                                  size='mini'
                                  weight='medium'
                                  className={cx('issue-details__issue-column')}
                                  data-elm-id={`${elmId}-issue-${group.type}-${
                                    row.rowIndex ?? 'none'
                                  }-${issueIdx}-column`}
                                >
                                  {issue.column}
                                </Text>
                                <Text
                                  size='mini'
                                  color='grayDark'
                                  data-elm-id={`${elmId}-issue-${group.type}-${
                                    row.rowIndex ?? 'none'
                                  }-${issueIdx}-description`}
                                >
                                  {issue.description}
                                </Text>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default IssueDetails
